import api from './api';

export interface CollectorConfig {
  id?: string;
  name: string;
  collector_type: string;
  display_name: string;
  description?: string;
  config_data: Record<string, any>;
  is_enabled: boolean;
  collection_interval?: number;
  rate_limit?: number;
  timeout?: number;
  max_retries?: number;
  retry_delay?: number;
  failure_threshold?: number;
  recovery_timeout?: number;
}

export interface CollectorResponse {
  id: string;
  name: string;
  collector_type: string;
  enabled: boolean;
  status: 'running' | 'stopped' | 'error' | 'starting' | 'stopping';
  config_data: Record<string, any>;
  collection_interval: number;
  description?: string;
  created_at: string;
  updated_at: string;
}

export interface CollectorStatus {
  name: string;
  status: string;
  enabled: boolean;
  is_running: boolean;
  last_run?: string;
  next_run?: string;
  health: {
    status: 'healthy' | 'unhealthy' | 'unknown';
    last_check: string;
    response_time?: number;
    error_message?: string;
  };
  metrics: {
    total_events: number;
    success_rate: number;
    avg_response_time: number;
    events_last_24h: number;
  };
  circuit_breaker: {
    state: 'CLOSED' | 'OPEN' | 'HALF_OPEN';
    failure_count: number;
    last_failure?: string;
  };
}

export interface HealthCheck {
  id: string;
  collector_name: string;
  status: 'healthy' | 'unhealthy';
  response_time?: number;
  error_message?: string;
  checked_at: string;
}

export interface Metrics {
  id: string;
  collector_name: string;
  events_collected: number;
  success_rate: number;
  avg_response_time: number;
  errors_count: number;
  recorded_at: string;
}

export interface CollectorLog {
  id: string;
  collector_name: string;
  level: 'INFO' | 'WARNING' | 'ERROR' | 'DEBUG';
  message: string;
  details?: Record<string, any>;
  created_at: string;
}

export interface DashboardData {
  summary: {
    total_collectors: number;
    running_collectors: number;
    healthy_collectors: number;
    total_events_today: number;
  };
  recent_health: HealthCheck[];
  recent_metrics: Metrics[];
  collector_types: string[];
}

export interface AvailableCollector {
  type: string;
  name: string;
  description: string;
  category: string;
  requires_api_key: boolean;
  icon: string;
  is_configured: boolean;
  is_enabled: boolean;
  status: string;
  last_run?: string;
  config_id?: number;
}

class CollectorsService {
  // Collector CRUD operations
  async getCollectors(): Promise<CollectorResponse[]> {
    const response = await api.get('/api/v2/collectors/');
    return response.data;
  }

  async createCollector(collector: CollectorConfig): Promise<CollectorResponse> {
    const response = await api.post('/api/v2/collectors/', collector);
    return response.data;
  }

  async updateCollector(name: string, updates: Partial<CollectorConfig>): Promise<CollectorResponse> {
    const response = await api.put(`/api/v2/collectors/${name}`, updates);
    return response.data;
  }

  async deleteCollector(name: string): Promise<void> {
    await api.delete(`/api/v2/collectors/${name}`);
  }

  // Collector control operations
  async startCollector(name: string): Promise<{ message: string }> {
    const response = await api.post(`/api/v2/collectors/${name}/start`);
    return response.data;
  }

  async stopCollector(name: string): Promise<{ message: string }> {
    const response = await api.post(`/api/v2/collectors/${name}/stop`);
    return response.data;
  }

  async restartCollector(name: string): Promise<{ message: string }> {
    const response = await api.post(`/api/v2/collectors/${name}/restart`);
    return response.data;
  }

  // Status and monitoring
  async getCollectorStatus(name: string): Promise<CollectorStatus> {
    const response = await api.get(`/api/v2/collectors/${name}/status`);
    return response.data;
  }

  async getHealthChecks(name: string, limit: number = 50): Promise<HealthCheck[]> {
    const response = await api.get(`/api/v2/collectors/${name}/health`, {
      params: { limit },
    });
    return response.data;
  }

  async getMetrics(name: string, limit: number = 50): Promise<Metrics[]> {
    const response = await api.get(`/api/v2/collectors/${name}/metrics`, {
      params: { limit },
    });
    return response.data;
  }

  async getLogs(name: string, limit: number = 100, level?: string): Promise<CollectorLog[]> {
    const response = await api.get(`/api/v2/collectors/${name}/logs`, {
      params: { limit, level },
    });
    return response.data;
  }

  // Dashboard and analytics
  async getDashboardData(): Promise<DashboardData> {
    const response = await api.get('/api/v2/collectors/dashboard');
    return response.data;
  }

  async getCollectorTypes(): Promise<string[]> {
    const response = await api.get('/api/v2/collectors/types');
    return response.data.types.map((type: any) => type.value);
  }

  // Configuration validation and schemas
  async getCollectorSchema(collectorType: string): Promise<any> {
    const response = await api.get(`/api/v2/collectors/schemas/${collectorType}`);
    return response.data;
  }

  async validateCollectorConfig(
    collectorType: string,
    configData: Record<string, any>,
  ): Promise<{
    valid: boolean;
    errors: string[];
    warnings: string[];
    connectivity_test: {
      success: boolean;
      message: string;
    };
  }> {
    const response = await api.post('/api/v2/collectors/validate-config', {
      collector_type: collectorType,
      config_data: configData,
    });
    return response.data;
  }

  // New methods for available collectors
  async getAvailableCollectors(): Promise<AvailableCollector[]> {
    const response = await api.get('/api/v2/collectors/available');
    return response.data;
  }

  async configureCollector(
    collectorType: string,
    configData: Record<string, any>,
  ): Promise<{
    message: string;
    config_id: number;
    is_new: boolean;
  }> {
    const response = await api.post(`/api/v2/collectors/configure/${collectorType}`, configData);
    return response.data;
  }

  // Migration functionality
  async migrateEnvConfigs(): Promise<{
    message: string;
    migrated: Array<{
      collector_type: string;
      config_keys: string[];
      has_credentials: boolean;
    }>;
    skipped: Array<{
      collector_type: string;
      reason: string;
    }>;
  }> {
    const response = await api.post('/api/v2/collectors/migrate-env-configs');
    return response.data;
  }
}

export const collectorsService = new CollectorsService();
